import _ from "lodash";
import { productTypes } from "../helpers";
import { InlineIcon } from "@iconify/react";
import React, { useContext, useEffect } from "react";
import {
  StyleTypeContext,
  TranslationContext,
} from "../../../../umbilical_tools";
import jssVariables from "../../../../jss_setup";
import { modalGenericStyles } from "../../../../jss_setup/modals";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { getActivitySupplierVoucher } from "../../../../api/activities";
import { findBrowser } from "../../../../helpers";
import { NoScrollContext } from "../../../../context";

var asset_s3 = import.meta.env.VITE_APP_S3_ASSETS_URL || "";

const b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export async function downloadActivityVoucher({ reservation }) {
  var detailed_data = reservation?.service_data?.detailed_data;
  if (!detailed_data) {
    detailed_data = reservation?.service_data?.details;
  }

  const file_name = reservation?.name || "voucher";

  const activity_uuid = detailed_data?.product_code;
  const supplier_reservation_uid = reservation?.supplier_reservation_uid;
  const provider = detailed_data?.provider;
  const api_uid = detailed_data?.api_uid;

  const result = await getActivitySupplierVoucher({
    activity_uuid,
    supplier_reservation_uid,
    provider,
    api_uid,
  });

  if (!result) {
    toast.error("Failed to get voucher. We are sorry for the inconvenience.", {
      autoClose: 5000,
    });
    return;
  }

  let url = window.URL.createObjectURL(b64toBlob(result, "application/pdf"));
  if (findBrowser() === "safari") {
    window.location = url;
  } else {
    const linkSource = `data:application/pdf;base64,${result}`;
    const downloadLink = document.createElement("a");
    const fileName = `${file_name}---Voucher.pdf`;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }
}

const styles = createUseStyles({
  ...modalGenericStyles,
  ViewDetailsModal: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 9999,
    ...(props?.customStyles?.ViewDetailsModal ?? {}),
  }),
  modal: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    background: "#FAFAFA",
    position: "relative",
    top: `calc(${jssVariables.navbar_height} + ${jssVariables.double_gap})`,
    left: "50%",
    transform: "translateX(-50%)",
    padding: jssVariables.double_gap,
    borderRadius: jssVariables.border_radius,
    width: "90vw",
    maxHeight: "75vh",
    ...(props?.customStyles?.modal ?? {}),
  }),
  graphicsMobile: (props) => ({
    position: "absolute",
    bottom: jssVariables.navbar_height,
    right: 0,
    width: "100%",
    zIndex: 0,
    ...(props?.customStyles?.graphicsMobile ?? {}),
  }),
  graphicsDesktop: (props) => ({
    display: "none",
    position: "absolute",
    bottom: jssVariables.normal_gap,
    left: `calc(${jssVariables.normal_gap} * 4)`,
    right: 0,
    width: `calc(100% - (${jssVariables.normal_gap} * 4))`,
    zIndex: 0,
    ...(props?.customStyles?.graphicsDesktop ?? {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    fontSize: "x-large",
    fontWeight: "bold",
    ...(props?.customStyles?.header ?? {}),
  }),
  content: (props) => ({
    overflowY: "auto",
    zIndex: 1,
    ...(props?.customStyles?.content ?? {}),
  }),
  closeIcon: (props) => ({
    cursor: "pointer",
    fontSize: "xx-large",
    ...(props?.customStyles?.closeIcon ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    modal: (props) => ({
      width: "65rem",
      ...(props?.customStyles?.modal?.smallscreen ?? {}),
    }),
    graphicsMobile: (props) => ({
      display: "none",
      ...(props?.customStyles?.graphicsMobile?.smallscreen ?? {}),
    }),
    graphicsDesktop: (props) => ({
      display: "block",
      ...(props?.customStyles?.graphicsDesktop?.smallscreen ?? {}),
    }),
  },
});
export const ViewDetailsModal = ({ productType, onHide, children }) => {
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { t } = useContext(TranslationContext);
  const { brand } = useContext(StyleTypeContext);
  const classes = styles({});

  useEffect(() => {
    toggleNoScroll(true);
    return () => toggleNoScroll(false);
  }, []);

  return (
    <div className={classes.ViewDetailsModal}>
      <div className={classes.modal}>
        {["easy"].includes(brand) && (
          <React.Fragment>
            <img
              src={`${asset_s3}/circles_bg.svg`}
              alt="circles"
              className={classes.graphicsMobile}
            />
            <img
              src={`${asset_s3}/circles_bg_desktop.svg`}
              alt="circles"
              className={classes.graphicsDesktop}
            />
          </React.Fragment>
        )}
        <div className={classes.header}>
          {t(productTypes[productType])} {t("details")}
          <InlineIcon
            icon="ph:x-bold"
            className={classes.closeIcon}
            onClick={onHide}
          />
        </div>
        <div className={classes.content}>{children}</div>
      </div>
    </div>
  );
};
ViewDetailsModal.defaultProps = { productType: "", children: null };
ViewDetailsModal.propTypes = {
  productType: PropTypes.string.isRequired,
  onHide: PropTypes.func.isRequired,
  children: PropTypes.node,
};
